import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";

import { Button, Heading, Paragraph } from "@fronterahealth/frontera-ui-components";

import { useGoalGenerationRules, useUpdateGoalGenerationRules } from "@api/rest/goal-generation-rules";
import type {
  GoalGenerationRulesGoalField,
  GoalGenerationRulesSharedGoalField,
  PutGoalGenerationRulesInput,
} from "@api/rest/goal-generation-rules";
import { InstructionsSourceEnum } from "@api/rest/types-and-hooks";
import { HoverTooltip, InfoTooltip } from "@components/InfoTooltip";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { ReportType } from "@pages/Templates/constants";

interface GoalGenerationRulesSectionProps {
  orgId: string;
}

const migrationMarkerKey = (orgId: string) => `goalRulesFieldMigrationOffered:${orgId}`;

/** Instructions state is scoped per (template, field) — PROA-2618 stage 1.
 *  Composite key for the pending/saved maps: template_id is a numeric pk
 *  string, so the U+241F unit separator can't appear in it and the key is
 *  unambiguous. Same-named templates sharing a template_id share one entry —
 *  correct, they're the same write address. The AI switch stays keyed
 *  org-wide by bare field key. */
const instructionsKey = (f: Pick<GoalGenerationRulesGoalField, "template_id" | "key">) => `${f.template_id}␟${f.key}`;

/** Overlay a pending edit, DROPPING the entry when the value matches the
 *  saved baseline: a reverted edit is no edit, so dirty detection and the
 *  "org default" badge (which keys off entry PRESENCE) stay truthful —
 *  e.g. typing into an inherited row and deleting it all restores the
 *  badge instead of stranding a not-dirty pending "". */
const withPendingEdit = (
  prev: Record<string, string>,
  key: string,
  value: string,
  baseline: string,
): Record<string, string> => {
  if (value !== baseline) return { ...prev, [key]: value };
  if (!(key in prev)) return prev;
  const next = { ...prev };
  delete next[key];
  return next;
};

/** A field takes instructions while the AI fills it — including goal_name,
 *  whose switch is locked ON. Platform/not-text locks are filled elsewhere
 *  or not at all, so no input. Applies to template rows and shared
 *  (org-default) entries alike. */
const takesInstructions = (f: Pick<GoalGenerationRulesGoalField, "editable" | "lock_reason">, ai: boolean) =>
  ai && (f.editable || f.lock_reason === "core");

// Tinted full-width header bars so the shared section and template buckets
// stay scannable when their field lists are expanded between them.
const headerBarClass =
  "mt-2 flex w-full min-w-0 items-center gap-1.5 rounded-md bg-limestone-100 px-2.5 py-1.5 text-left";

/**
 * One-time migration aid: pull legacy "Field name: text" paragraphs out
 * of the general blob and next to their fields. Blocks are blank-line
 * separated; a block whose first line is "<known field name>: ..." moves
 * to that field, everything else stays general.
 */
function splitLegacyFieldBlocks(general: string, fields: GoalGenerationRulesGoalField[]) {
  const keyByName = new Map<string, string>();
  for (const f of fields) {
    if (!takesInstructions(f, f.ai)) continue;
    keyByName.set((f.title ?? f.field_identifier).trim().toLowerCase(), f.key);
    if (f.field_identifier) keyByName.set(f.field_identifier.trim().toLowerCase(), f.key);
  }
  const extracted: Record<string, string> = {};
  const remaining: string[] = [];
  for (const block of general.split(/\n\s*\n/)) {
    const lines = block.split("\n");
    const head = lines[0].match(/^\s*(.+?)\s*:\s*(.*)$/);
    const key = head ? keyByName.get(head[1].toLowerCase()) : undefined;
    const text =
      head && key
        ? [head[2], ...lines.slice(1)]
            .map((l) => l.trim())
            .filter(Boolean)
            .join(" ")
        : "";
    if (key && text) {
      extracted[key] = extracted[key] ? `${extracted[key]} ${text}` : text;
    } else if (block.trim()) {
      remaining.push(block.trim());
    }
  }
  return { extracted, remaining: remaining.join("\n\n") };
}

const AISwitch: React.FC<{ label: string; on: boolean; disabled: boolean; busy: boolean; onToggle: () => void }> = ({
  label,
  on,
  disabled,
  busy,
  onToggle,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={`AI fills ${label}`}
    disabled={disabled || busy}
    onClick={onToggle}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-rio-300 ${
      on ? "bg-green-600" : "bg-limestone-300"
    } ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
  >
    <span
      aria-hidden
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        on ? "translate-x-[18px]" : "translate-x-0.5"
      }`}
    />
  </button>
);

/**
 * Org-level AI goal-generation rules editor (goal-customization-ai,
 * PROC-115). The general instructions blob, plus one row per (template,
 * field), each with an AI switch and — while AI is on — an instructions
 * input. The switch is ORG-WIDE per field key (shared across templates);
 * instructions are LAYERED (PROA-2618): the "Shared across templates"
 * section edits the ORG-DEFAULT text per field (PUT
 * org_field_instructions, merge), which every template row inherits —
 * shown with an "org default" badge — until the row carries its own
 * override, saved per template via the nested field_instructions shape.
 * Untouched inherited rows save as "" so org text is never copied onto
 * template rows. The backend stores the texts structured and concatenates
 * them onto the general blob as "Title: text" where the LLM consumes the
 * rules. A custom field's own instructions satisfy the naming requirement,
 * so typing "Field name: ..." by hand is no longer needed.
 */
export const GoalGenerationRulesSection: React.FC<GoalGenerationRulesSectionProps> = ({ orgId }) => {
  const { data, isLoading, isError } = useGoalGenerationRules(orgId);
  const updateMutation = useUpdateGoalGenerationRules(orgId);

  // Derived-state pattern (matches OrganizationSettingsTab): saved values
  // come from the query; pending edits overlay them until Save.
  const [pendingGeneral, setPendingGeneral] = useState<string | null>(null);
  // Org-wide by field key — one switch per field across all templates.
  const [pendingAI, setPendingAI] = useState<Record<string, boolean>>({});
  // Per (template, field) — keyed by instructionsKey(row), NOT bare key:
  // the same field carries independent instructions under each template.
  // An entry here is a TEMPLATE-LEVEL override; rows without one inherit
  // the org default (badged "org default" when that's what's displayed).
  const [pendingInstructions, setPendingInstructions] = useState<Record<string, string>>({});
  // The ORG-DEFAULT layer ("Shared across templates" section) — keyed by
  // bare field key; edits go to PUT org_field_instructions (merge).
  const [pendingOrgInstructions, setPendingOrgInstructions] = useState<Record<string, string>>({});
  const [migratedFromGeneral, setMigratedFromGeneral] = useState(false);

  // Legacy layout migration: field-specific "Name: text" paragraphs used
  // to live inside the general blob. When nothing is saved per-field yet,
  // move them next to their fields as PENDING edits — the user reviews
  // and saves the reorganized state once.
  useEffect(() => {
    if (!data || data.fields.some((f) => (f.instructions ?? "").trim())) return;
    if (
      pendingGeneral !== null ||
      Object.keys(pendingInstructions).length > 0 ||
      Object.keys(pendingOrgInstructions).length > 0
    )
      return;
    // A user who reviewed the migration and deliberately saved (in either
    // layout) shouldn't be re-nagged — the marker is written on SAVE, not
    // here, so a refresh/navigate-away before saving re-offers instead of
    // stranding the paragraphs in the general blob.
    if (localStorage.getItem(migrationMarkerKey(orgId))) return;
    const { extracted, remaining } = splitLegacyFieldBlocks(data.general ?? "", data.fields);
    if (Object.keys(extracted).length === 0) return;
    // The legacy blob text was org-wide, so its natural home is the
    // ORG-DEFAULT layer: keys in the shared section get ONE org entry every
    // template inherits (nothing materialized onto rows). Keys the shared
    // section can't address (not enabled on every template) fall back to
    // per-template seeding under every row carrying the key (hidden rows
    // too) so the text stays reviewable and lands everywhere it applied.
    const sharedKeys = new Set(data.shared_fields.map((s) => s.key));
    const orgSeed: Record<string, string> = {};
    for (const [key, text] of Object.entries(extracted)) {
      if (sharedKeys.has(key)) orgSeed[key] = text;
    }
    const perRow: Record<string, string> = {};
    for (const f of data.fields) {
      if (!sharedKeys.has(f.key) && extracted[f.key] !== undefined) perRow[instructionsKey(f)] = extracted[f.key];
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPendingInstructions(perRow);
    setPendingOrgInstructions(orgSeed);
    setPendingGeneral(remaining);
    setMigratedFromGeneral(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Collapse state per template section. Unset = collapsed by default.
  const [expandedBuckets, setExpandedBuckets] = useState<Record<string, boolean>>({});
  // The "Shared across templates" (org-default) section — own flag, not an
  // expandedBuckets entry: default EXPANDED, and a template named "Shared
  // across templates" mustn't collide with it.
  const [sharedExpanded, setSharedExpanded] = useState(true);

  // One collapsible section per template, grouped by (report_type, name):
  // IA and reassessment templates both run goal generation, and a
  // same-named pair must stay two buckets (different templates, different
  // write addresses). Core fields ride along inline (locked-on), and a
  // field shared across templates appears under each: its AI switch is
  // org-wide (keyed by field key) but its instructions are per-template
  // (keyed by template_id + key). Buckets that share a template_id
  // (same-named templates on one TPT) genuinely share instructions state —
  // no special-casing. A field disabled on a template is hidden for that
  // template — a disabled field can't generate there anyway.
  const fieldBuckets = useMemo(() => {
    const fields = data?.fields ?? [];
    const byTemplate = new Map<
      string,
      { name: string; reassessment: boolean; fields: GoalGenerationRulesGoalField[] }
    >();
    // Rows with no template attribution — shouldn't normally happen, but
    // must stay visible rather than silently vanish.
    const ungrouped: GoalGenerationRulesGoalField[] = [];
    for (const f of fields) {
      // Disabled on this template → not shown here (it can't generate until
      // it's re-enabled on the Programming tab).
      if (!f.is_enabled) continue;
      if (!f.template_name) {
        ungrouped.push(f);
        continue;
      }
      const bucketKey = `${f.report_type}␟${f.template_name}`;
      const bucket = byTemplate.get(bucketKey) ?? {
        name: f.template_name,
        reassessment: f.report_type === ReportType.REASSESSMENT,
        fields: [],
      };
      bucket.fields.push(f);
      byTemplate.set(bucketKey, bucket);
    }
    const buckets: {
      /** Stable identity for React keys + collapse state — label alone
       *  collides for a same-named IA/reassessment pair. */
      key: string;
      label: string;
      tooltip: string;
      reassessment: boolean;
      fields: GoalGenerationRulesGoalField[];
      defaultExpanded: boolean;
      collapsible: boolean;
    }[] = [];
    const sorted = [...byTemplate.entries()].sort(
      ([, a], [, b]) => a.name.localeCompare(b.name) || Number(a.reassessment) - Number(b.reassessment),
    );
    for (const [bucketKey, group] of sorted) {
      buckets.push({
        key: bucketKey,
        label: group.name,
        tooltip: group.name,
        reassessment: group.reassessment,
        fields: group.fields,
        defaultExpanded: false,
        collapsible: true,
      });
    }
    if (ungrouped.length > 0) {
      buckets.push({
        key: "__ungrouped__",
        label: "Other fields",
        tooltip: "Fields not attributed to a specific template.",
        reassessment: false,
        fields: ungrouped,
        defaultExpanded: true,
        collapsible: true,
      });
    }
    return buckets;
  }, [data]);

  if (isLoading) {
    return <div className="h-24 animate-pulse rounded-xl bg-limestone-100" />;
  }
  // Only bail when there's nothing to render — a failed background
  // refetch keeps stale data (and the user's pending edits) on screen.
  if (!data) {
    return (
      <Paragraph size="small" colorType="secondary">
        {isError ? "Couldn't load goal generation rules." : null}
      </Paragraph>
    );
  }

  const general = pendingGeneral ?? data.general ?? "";
  const sharedFields = data.shared_fields ?? [];
  const savedAI = Object.fromEntries(data.fields.map((f) => [f.key, f.ai]));
  // Baseline per ROW (template + field) at the TEMPLATE level: an inherited
  // row's own text is empty (the displayed org text isn't the row's), so
  // typing the org text back in still registers as a new override.
  const savedInstructions = Object.fromEntries(
    data.fields.map((f) => [
      instructionsKey(f),
      f.instructions_source === InstructionsSourceEnum.template ? (f.instructions ?? "") : "",
    ]),
  );
  // Org-default layer baseline, keyed by bare field key.
  const savedOrgInstructions = Object.fromEntries(sharedFields.map((s) => [s.key, s.org_instructions ?? ""]));
  const aiChanges = Object.fromEntries(Object.entries(pendingAI).filter(([key, value]) => savedAI[key] !== value));
  const instructionChanges = Object.entries(pendingInstructions).filter(
    ([rowKey, value]) => rowKey in savedInstructions && (savedInstructions[rowKey] ?? "") !== value,
  );
  const orgInstructionChanges = Object.entries(pendingOrgInstructions).filter(
    ([key, value]) => key in savedOrgInstructions && (savedOrgInstructions[key] ?? "") !== value,
  );
  const isDirty =
    (pendingGeneral !== null && pendingGeneral !== (data.general ?? "")) ||
    Object.keys(aiChanges).length > 0 ||
    instructionChanges.length > 0 ||
    orgInstructionChanges.length > 0;

  const handleSave = async () => {
    try {
      // Send the FULL switch state, not just changed keys: the backend
      // normalizes templates that drifted (e.g. a field added to a
      // template after the last save defaults to not-AI under an ON
      // switch).
      const fullAIState = Object.fromEntries(
        data.fields
          .filter((f) => f.editable || (f.can_turn_off && pendingAI[f.key] === false))
          .map((f) => [f.key, pendingAI[f.key] ?? f.ai]),
      );
      // Nested per-template shape, whole-map semantics PER template: send
      // every template's every key (rows hidden as disabled included, so
      // their dormant texts survive). Each entry is the row's TEMPLATE-LEVEL
      // text only — an untouched inherited row sends "" so org-default text
      // is never materialized onto template rows (the no-materialization
      // rule); typing on a row creates its override. Instructions persist
      // independent of the AI switch: toggling a field off keeps its text
      // dormant instead of deleting it. Duplicate (template_id, key) rows
      // (same-named templates sharing a TPT) collapse onto one entry —
      // their state is identical by construction.
      const fullInstructions: Record<string, Record<string, string>> = {};
      for (const f of data.fields) {
        const perTemplate = (fullInstructions[f.template_id] ??= {});
        perTemplate[f.key] = (
          pendingInstructions[instructionsKey(f)] ??
          (f.instructions_source === InstructionsSourceEnum.template ? (f.instructions ?? "") : "")
        ).trim();
      }
      // Org-default layer: ONLY the keys the user actually changed in the
      // shared section — merge semantics server-side make changed-keys
      // sufficient (a cleared key sends "" to delete). Echoing every key
      // would clobber same-save row edits under non-org-linked templates:
      // the backend routes those into the org map FIRST and applies this
      // map AFTER (org wins), so a stale echo overwrites or deletes them.
      const orgInstructions = Object.fromEntries(orgInstructionChanges.map(([key, value]) => [key, value.trim()]));
      const payload: PutGoalGenerationRulesInput = {
        general: general.trim() || null,
        ai_fields: fullAIState,
      };
      // OMIT empty maps rather than sending {}: the backend sniffs {} as
      // the legacy FLAT field_instructions shape, which 400s combined with
      // org_field_instructions — and would block general-text-only saves
      // on orgs with no goal fields. (Most saves carry no org changes, so
      // org_field_instructions is usually absent.)
      if (Object.keys(fullInstructions).length > 0) payload.field_instructions = fullInstructions;
      if (Object.keys(orgInstructions).length > 0) payload.org_field_instructions = orgInstructions;
      await updateMutation.mutateAsync(payload);
      notifySuccess("Goal generation rules updated");
      if (migratedFromGeneral) {
        localStorage.setItem(migrationMarkerKey(orgId), "1");
      }
      setPendingGeneral(null);
      setPendingAI({});
      setPendingInstructions({});
      setPendingOrgInstructions({});
      setMigratedFromGeneral(false);
    } catch (err) {
      // Pending edits stay so the user can fix and re-save (e.g. add the
      // instructions a custom field needs).
      notifyError(err instanceof Error ? err.message : "Failed to update goal generation rules");
    }
  };

  const renderFieldRow = (f: GoalGenerationRulesGoalField) => {
    const ai = pendingAI[f.key] ?? f.ai;
    // not_text locks may still be switched OFF (drift cleanup).
    const locked = !f.editable && !(f.can_turn_off && ai);
    const lockText =
      f.lock_reason === "not_text"
        ? "Only text fields can be filled by the AI" +
          (f.can_turn_off && ai ? " — this one can only be turned off." : ".")
        : f.lock_reason === "platform"
          ? "This field is filled by a different AI workflow — its setting is managed by the platform."
          : "Core field — its AI setting is managed by the platform and can't be changed here.";
    // Bound per (template, field): editing this row never touches the same
    // field's textarea under another template.
    const pendingRow = pendingInstructions[instructionsKey(f)];
    // Rows that WOULD inherit the org layer: no pending row edit (reverted
    // edits are dropped by withPendingEdit) and no row text of their own —
    // source "org" (inheriting a saved default today) or null (nothing
    // resolved yet, so a first-time org default typed above lands here on
    // save).
    const wouldInherit = pendingRow === undefined && f.instructions_source !== InstructionsSourceEnum.template;
    // Badge = the row is currently displaying ORG-LAYER text: a saved org
    // default (source "org"), or a not-yet-saved first-time default being
    // typed in the shared section and live-previewed on this row — so the
    // preview reads as org-layer text, not the row's own.
    const inherited =
      wouldInherit && (f.instructions_source === InstructionsSourceEnum.org || !!pendingOrgInstructions[f.key]);
    // Display stays the RESOLVED text (org-layer text shows and is editable
    // in place; typing creates this row's pending template override) —
    // would-inherit rows track shared-section edits above LIVE, including
    // first-time defaults on rows where nothing resolved yet. The save
    // payload is unaffected: these rows still send "" (no materialization).
    const instructions =
      pendingRow ?? (wouldInherit ? (pendingOrgInstructions[f.key] ?? f.instructions ?? "") : (f.instructions ?? ""));
    return (
      <div key={f.key} className="flex flex-col px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 flex-col">
            <span className="flex items-center gap-2 text-base text-text-primary">
              {f.title ?? f.field_identifier}
              {f.custom && (
                <span className="rounded bg-limestone-100 px-1.5 py-0.5 font-mono text-[11px] uppercase tracking-wide text-text-secondary">
                  custom
                </span>
              )}
              {inherited && (
                <span className="rounded bg-limestone-100 px-1.5 py-0.5 font-mono text-[11px] uppercase tracking-wide text-text-secondary">
                  org default
                </span>
              )}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {(locked || !f.editable) && (
              <InfoTooltip
                text={lockText}
                label={`Why ${f.title ?? f.field_identifier} is locked`}
                iconClassName="h-4 w-4 text-text-tertiary"
              />
            )}
            <span className={`text-sm ${ai ? "font-medium text-green-700" : "text-text-tertiary"}`}>
              {ai ? "AI" : "Clinician"}
            </span>
            <AISwitch
              label={f.title ?? f.field_identifier}
              on={ai}
              disabled={locked}
              busy={updateMutation.isPending}
              onToggle={() => setPendingAI((prev) => ({ ...prev, [f.key]: !ai }))}
            />
          </span>
        </div>
        {takesInstructions(f, ai) && (
          <>
            {/* Template-row inputs only (the shared section's textareas edit
                the org layer itself, so the override hint would be wrong). */}
            <span className="mt-2 flex items-center gap-1.5 text-xs font-medium text-text-tertiary">
              Instructions
              <InfoTooltip
                text="Instructions here override the org-level settings above for this template."
                label="About template instructions"
                iconClassName="h-3.5 w-3.5 text-text-tertiary"
              />
            </span>
            <textarea
              aria-label={`Instructions for ${f.title ?? f.field_identifier}`}
              value={instructions}
              onChange={(e) =>
                setPendingInstructions((prev) =>
                  withPendingEdit(
                    prev,
                    instructionsKey(f),
                    e.target.value,
                    savedInstructions[instructionsKey(f)] ?? "",
                  ),
                )
              }
              disabled={updateMutation.isPending}
              rows={2}
              placeholder={
                f.custom
                  ? "What should the AI write here? (required for custom fields)"
                  : "Instructions for this field (optional)"
              }
              className="mt-1 w-full rounded-md border border-border-medium bg-bg-default px-2.5 py-1.5 text-sm leading-relaxed text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-rio-300"
            />
          </>
        )}
      </div>
    );
  };

  // "Shared across templates" row: edits the ORG-DEFAULT text for the key —
  // the layer every template row inherits until it carries its own override.
  // No AI switch here (switches live on template rows); same instructions
  // gating as rows so the section mirrors what the AI actually consumes.
  const renderSharedFieldRow = (s: GoalGenerationRulesSharedGoalField) => {
    const value = pendingOrgInstructions[s.key] ?? s.org_instructions ?? "";
    return (
      <div key={s.key} className="flex flex-col px-4 py-3">
        <span className="flex items-center gap-2 text-base text-text-primary">
          {s.title ?? s.field_identifier}
          {s.custom && (
            <span className="rounded bg-limestone-100 px-1.5 py-0.5 font-mono text-[11px] uppercase tracking-wide text-text-secondary">
              custom
            </span>
          )}
        </span>
        <textarea
          aria-label={`Org default instructions for ${s.title ?? s.field_identifier}`}
          value={value}
          onChange={(e) =>
            setPendingOrgInstructions((prev) =>
              withPendingEdit(prev, s.key, e.target.value, savedOrgInstructions[s.key] ?? ""),
            )
          }
          disabled={updateMutation.isPending}
          rows={2}
          placeholder={
            s.custom
              ? "What should the AI write here? (required for custom fields)"
              : "Default instructions for every template (optional)"
          }
          className="mt-2 w-full rounded-md border border-border-medium bg-bg-default px-2.5 py-1.5 text-sm leading-relaxed text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-rio-300"
        />
      </div>
    );
  };

  // Only shared entries whose field currently takes instructions render —
  // live against the pending AI switches, like the template rows.
  const sharedInstructionFields = sharedFields.filter((s) => takesInstructions(s, pendingAI[s.key] ?? s.ai));

  return (
    <section className="flex flex-col gap-5 rounded-xl border border-border-medium bg-bg-surface p-6">
      {/* Sticky header keeps Save reachable while editing a long field
          list (the old footer scrolled away). */}
      <div className="sticky top-0 z-10 -mx-6 -mt-6 flex items-center justify-between gap-4 rounded-t-xl border-b border-border-light bg-bg-surface px-6 pb-3 pt-6">
        <div className="flex items-center gap-2">
          <Heading size="small">AI Goal Generation Rules</Heading>
          <InfoTooltip
            label="About AI goal generation rules"
            text="Instructions the AI follows when generating treatment-plan goals for this organization. Changes apply to goals generated from now on — existing reports keep the instructions they were written under."
            iconClassName="h-4 w-4 text-text-tertiary"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-text-tertiary">
            {migratedFromGeneral && (instructionChanges.length > 0 || orgInstructionChanges.length > 0)
              ? "Field-specific paragraphs were moved next to their fields — review and save."
              : isDirty
                ? "Unsaved changes"
                : "All changes saved"}
          </span>
          <Button
            variant="primary"
            buttonStyle="filled"
            size="sm"
            text={updateMutation.isPending ? "Saving..." : "Save rules"}
            disabled={!isDirty || updateMutation.isPending}
            onClick={handleSave}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-4">
          <span className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider text-text-tertiary">
            General instructions
            <InfoTooltip
              label="About general instructions"
              text="Org-wide conventions that apply to every goal — tone, language, mastery criteria style. Guidance for one specific field belongs in that field's input below."
              iconClassName="h-4 w-4 text-text-tertiary"
            />
          </span>
        </div>
        <textarea
          aria-label="Goal generation instructions"
          value={general}
          onChange={(e) => setPendingGeneral(e.target.value)}
          disabled={updateMutation.isPending}
          rows={6}
          placeholder={
            "Org-wide conventions that apply to every goal - instructions for a specific field belong in that field's input below.\n\nFor example:\nUse neurodiversity-affirming language and avoid deficit framing.\nWrite mastery criteria as accuracy across consecutive sessions unless the payor requires otherwise.\nNever reference specific staff members by name."
          }
          className="min-h-[160px] w-full rounded-lg border border-border-medium bg-bg-default p-3 text-base leading-relaxed text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-rio-300"
        />
      </div>

      {fieldBuckets.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between">
            <span className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider text-text-tertiary">
              Fields the AI fills
              <InfoTooltip
                label="About fields the AI fills"
                text="Flip a switch to control which fields the AI writes — it applies to every template this organization uses. Shared across templates sets each field's default instructions; the templates below can override them per field. Custom fields need instructions naming them (saved) before AI can be turned on."
                iconClassName="h-4 w-4 text-text-tertiary"
              />
            </span>
            <span className="text-sm text-text-tertiary">From your templates</span>
          </div>
          {sharedInstructionFields.length > 0 && (
            <div className="flex flex-col gap-1">
              {/* The tooltip is a button, so it sits beside the toggle, not
                  inside it (no nested buttons). The toggle's ::after covers
                  the whole bar so it stays clickable edge to edge, like the
                  bucket headers; the tooltip sits above it. */}
              <div className={`${headerBarClass} relative hover:bg-limestone-200`}>
                <button
                  type="button"
                  onClick={() => setSharedExpanded((prev) => !prev)}
                  className="flex min-w-0 items-center gap-1.5 rounded-sm text-left after:absolute after:inset-0 after:rounded-md focus:outline-none focus:ring-1 focus:ring-rio-300"
                  aria-expanded={sharedExpanded}
                >
                  {sharedExpanded ? (
                    <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
                  ) : (
                    <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
                  )}
                  <span className="truncate text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Shared across templates
                  </span>
                </button>
                <span className="relative z-10 inline-flex">
                  <InfoTooltip
                    text="Applies to every template. Templates below can override individual fields."
                    label="About shared instructions"
                    iconClassName="h-3.5 w-3.5 text-text-tertiary"
                  />
                </span>
                <span className="shrink-0 rounded-full bg-white px-1.5 text-[11px] tabular-nums text-text-secondary">
                  {sharedInstructionFields.length}
                </span>
              </div>
              {sharedExpanded && (
                <div className="flex flex-col divide-y divide-border-light rounded-lg border border-border-light">
                  {sharedInstructionFields.map((s) => renderSharedFieldRow(s))}
                </div>
              )}
            </div>
          )}
          {fieldBuckets.map((bucket) => {
            const expanded = !bucket.collapsible || (expandedBuckets[bucket.key] ?? bucket.defaultExpanded);
            const header = (
              <>
                <HoverTooltip text={bucket.tooltip} className="max-w-full">
                  <span className="truncate text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    {bucket.label}
                  </span>
                </HoverTooltip>
                {bucket.reassessment && (
                  <span className="shrink-0 rounded bg-white px-1.5 py-0.5 font-mono text-[11px] uppercase tracking-wide text-text-secondary">
                    reassessment
                  </span>
                )}
                <span className="shrink-0 rounded-full bg-white px-1.5 text-[11px] tabular-nums text-text-secondary">
                  {bucket.fields.length}
                </span>
              </>
            );
            return (
              <div key={bucket.key} className="flex flex-col gap-1">
                {bucket.collapsible ? (
                  <button
                    type="button"
                    onClick={() => setExpandedBuckets((prev) => ({ ...prev, [bucket.key]: !expanded }))}
                    className={`${headerBarClass} hover:bg-limestone-200 focus:outline-none focus:ring-1 focus:ring-rio-300`}
                    aria-expanded={expanded}
                  >
                    {expanded ? (
                      <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
                    ) : (
                      <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
                    )}
                    {header}
                  </button>
                ) : (
                  <div className={headerBarClass}>{header}</div>
                )}
                {expanded && (
                  <div className="flex flex-col divide-y divide-border-light rounded-lg border border-border-light">
                    {bucket.fields.map((f) => renderFieldRow(f))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
